import { callApi } from "./api";
import {
  collectDeedDocumentEntriesInSaveOrder,
  deedDocumentEntryVisible,
  DeedDocumentEntryInOrder,
  fetchScrutinyChecklistForFile,
  formatAddressOfPropertyDisplayValue,
  isAddressOfPropertyMappedField,
  REPEATABLE_DOC_NO_META_KEY,
  sortRepeatableRowIndices,
} from "./dynamicChecklistScrutinyPrefill";
import {
  ChecklistFieldDef,
  ChecklistFieldType,
  ChecklistRepeatableGroupDef,
  ChecklistSectionDef,
  DropdownOptionFollowUpDef,
  bankLinkedGroups,
  branchKeyForFollowUpAnswer,
  fieldIdsInDeedGroups,
  fieldTypeAllowsVerifyCheckbox,
  isChecklistAnswerFilled,
  labelForParentFollowBranch,
  repeatableEntriesKey,
  sectionFlowMode,
  sectionGateFields,
  normalizeMasterFromApi,
  sortSections,
  triggerFollowBranchKey,
  triggerFollowBranchKeyForValue,
} from "../components/DynamicChecklistManagement/DynamicChecklistManagement";
import {
  EC_HIERARCHY_META,
  ecBlockGroup,
  ecEntryGroup,
  ecLinkedGroups,
  entryRowsForBlock,
} from "./dynamicChecklistEcVerification";
import {
  matchNaturePropertyKey,
  naturePropertyLabel,
  resolveVisibleSurveyNatureGroups,
  surveyGroupsVisibleForFile,
  surveyLinkedGroups,
  surveyNatureFieldScopeKey,
} from "./dynamicChecklistSurveyNature";
import {
  GeneralDetailsDocumentHierarchy,
  GeneralDetailsScheduleRow,
  hierarchyRowIdentity,
  flattenSurveyHierarchyRows,
  SURVEY_HIERARCHY_META,
  scheduleDisplayNumber,
  SurveyHierarchyLevel,
  surveyHierarchyGroupForLevel,
  surveyHierarchyGroupHasChecklistFields,
  surveyHierarchyLinkedGroups,
  surveySectionUsesHierarchy,
} from "./dynamicChecklistSurveyHierarchy";
import { DynamicChecklistRuntimeValue } from "../components/DynamicChecklistRuntime/DynamicChecklistRuntime";

export interface ExportFieldRow {
  key: string;
  label: string;
  displayValue: string;
  /** `null` when this field has no verification checkbox in the master. */
  verified: boolean | null;
  indent?: number;
}

export interface ExportSubBlock {
  title?: string;
  rows: ExportFieldRow[];
}

export interface ExportSection {
  id: string;
  title: string;
  headerVariant?: "default" | "warning";
  blocks: ExportSubBlock[];
}

export interface DynamicChecklistExportDocument {
  sections: ExportSection[];
  remarks?: string;
}

/** Optional scrutiny context so survey verification export matches checklist runtime. */
export interface DynamicChecklistExportContext {
  generalDetailsByDocument?: Record<string, GeneralDetailsDocumentHierarchy>;
}

export async function fetchDynamicChecklistExportContext(
  fileId: string | number
): Promise<DynamicChecklistExportContext> {
  const scrutiny = await fetchScrutinyChecklistForFile(fileId);
  const gd =
    scrutiny?.generalDetailsByDocument && typeof scrutiny.generalDetailsByDocument === "object"
      ? (scrutiny.generalDetailsByDocument as Record<string, GeneralDetailsDocumentHierarchy>)
      : undefined;
  return { generalDetailsByDocument: gd };
}

export function emptyDynamicChecklistExportContext(): Promise<DynamicChecklistExportContext> {
  return Promise.resolve({});
}

function labelsMatch(a: string | undefined, b: string | undefined): boolean {
  const aa = (a || "").trim().toLowerCase();
  const bb = (b || "").trim().toLowerCase();
  return !!aa && aa === bb;
}

function workFileFieldShowsVerify(field: ChecklistFieldDef): boolean {
  return fieldTypeAllowsVerifyCheckbox(field.type) && !!field.showVerifyCheckbox;
}

function repeatableRowVerifyKey(
  sectionId: string,
  groupId: string,
  rowIndex: number,
  fieldId: string
): string {
  return `${repeatableEntriesKey(sectionId, groupId)}::${rowIndex}::${fieldId}`;
}

function verifyFlag(
  fieldVerified: Record<string, string>,
  key: string,
  field: ChecklistFieldDef
): boolean | null {
  if (!workFileFieldShowsVerify(field)) return null;
  return fieldVerified[key] === "true";
}

export function parseChecklistPayload(currentJson: string | undefined | null): any | null {
  if (!currentJson) return null;
  try {
    return JSON.parse(currentJson);
  } catch {
    return null;
  }
}

export function extractDynamicSnapshot(parsed: any): DynamicChecklistRuntimeValue | null {
  if (!parsed || typeof parsed !== "object") return null;
  const dyn = parsed.dynamic;
  if (!dyn || typeof dyn !== "object") return null;
  return {
    checklistValues:
      dyn.checklistValues && typeof dyn.checklistValues === "object"
        ? { ...dyn.checklistValues }
        : {},
    repeatableValues:
      dyn.repeatableValues && typeof dyn.repeatableValues === "object"
        ? Object.keys(dyn.repeatableValues).reduce<Record<string, Record<string, string>[]>>(
            (acc, k) => {
              const rows = dyn.repeatableValues[k];
              acc[k] = Array.isArray(rows) ? rows.map((r: any) => ({ ...(r || {}) })) : [];
              return acc;
            },
            {}
          )
        : {},
    fieldVerified:
      dyn.fieldVerified && typeof dyn.fieldVerified === "object"
        ? { ...dyn.fieldVerified }
        : {},
  };
}

/** Saved checklist JSON includes a `dynamic` blob (work-file runtime). */
export function hasUsableDynamicPayload(parsed: any): boolean {
  return !!extractDynamicSnapshot(parsed);
}

export function fetchDynamicChecklistMaster(): Promise<ChecklistSectionDef[]> {
  return callApi("POST", "dynamic_checklist_master_get.php")
    .then((res) => res?.data)
    .then((response) => {
      const data = response?.data;
      if (response?.result === false || !data) return [];
      const raw = Array.isArray(data.master) ? data.master : [];
      return normalizeMasterFromApi(raw as ChecklistSectionDef[]);
    })
    .catch(() => [] as ChecklistSectionDef[]);
}

export function formatFieldDisplayValue(
  type: ChecklistFieldType,
  raw: string | undefined,
  field?: ChecklistFieldDef
): string {
  const v = raw ?? "";
  if (type === "checkbox") return v === "true" ? "Yes" : "No";
  if (type === "yes_no") return v === "Yes" || v === "No" ? v : "—";
  if (!v.trim()) return "—";
  if (field && isAddressOfPropertyMappedField(field)) {
    return formatAddressOfPropertyDisplayValue(v);
  }
  return v;
}

function rowIsMeaningful(
  type: ChecklistFieldType,
  raw: string | undefined,
  verified: boolean | null
): boolean {
  if (verified === true) return true;
  return isChecklistAnswerFilled(type, raw);
}

function pushRow(
  rows: ExportFieldRow[],
  key: string,
  label: string,
  type: ChecklistFieldType,
  raw: string | undefined,
  fieldVerified: Record<string, string>,
  verifyKey: string,
  field: ChecklistFieldDef,
  indent = 0
): void {
  const verified = verifyFlag(fieldVerified, verifyKey, field);
  if (!rowIsMeaningful(type, raw, verified)) return;
  rows.push({
    key,
    label,
    displayValue: formatFieldDisplayValue(type, raw, field),
    verified,
    indent,
  });
}

function collectVisibleFollowUpRows(
  nodes: DropdownOptionFollowUpDef[] | undefined,
  parentBranchKey: string,
  parentQuestionType: ChecklistFieldType,
  checklist: Record<string, string>,
  fieldVerified: Record<string, string>,
  pathPrefix: string,
  rows: ExportFieldRow[],
  indent: number,
  getFollowUpValue: (valueKey: string) => string,
  getFollowUpVerifyKey: (valueKey: string) => string
): void {
  if (!nodes?.length) return;
  const fu = nodes.find((x) => x.whenOption === parentBranchKey);
  if (!fu?.questionLabel.trim()) return;

  const synthetic: ChecklistFieldDef = {
    id: fu.valueKey,
    label: fu.questionLabel,
    type: fu.questionType,
    order: 0,
    required: fu.required,
  };

  pushRow(
    rows,
    getFollowUpVerifyKey(fu.valueKey),
    `${pathPrefix} → ${fu.questionLabel}`,
    fu.questionType,
    getFollowUpValue(fu.valueKey),
    fieldVerified,
    getFollowUpVerifyKey(fu.valueKey),
    synthetic,
    indent
  );

  if (
    fu.nestedFollowUpsEnabled &&
    fu.nestedFollowUps?.length &&
    (fu.questionType === "dropdown" ||
      fu.questionType === "checkbox" ||
      fu.questionType === "yes_no" ||
      fu.questionType === "text")
  ) {
    const subChecklist = { [fu.valueKey]: getFollowUpValue(fu.valueKey) };
    const subKey = branchKeyForFollowUpAnswer(fu, subChecklist);
    if ((fu.questionType === "dropdown" || fu.questionType === "yes_no") && !subKey) return;
    const branchLabel = labelForParentFollowBranch(fu.questionType, subKey);
    collectVisibleFollowUpRows(
      fu.nestedFollowUps,
      subKey,
      fu.questionType,
      checklist,
      fieldVerified,
      `${pathPrefix} → ${fu.questionLabel} (if “${branchLabel}”)`,
      rows,
      indent + 1,
      getFollowUpValue,
      getFollowUpVerifyKey
    );
  }
}

function collectFieldFollowUpRows(
  field: ChecklistFieldDef,
  raw: string | undefined,
  checklist: Record<string, string>,
  fieldVerified: Record<string, string>,
  pathPrefix: string,
  rows: ExportFieldRow[],
  getFollowUpValue: (valueKey: string) => string = (valueKey) => checklist[valueKey] ?? "",
  getFollowUpVerifyKey: (valueKey: string) => string = (valueKey) => valueKey
): void {
  if (!field.dropdownFollowUpsEnabled || !field.optionFollowUps?.length) return;
  if (
    field.type !== "dropdown" &&
    field.type !== "checkbox" &&
    field.type !== "yes_no" &&
    field.type !== "text"
  )
    return;
  const selected =
    raw !== undefined ? triggerFollowBranchKeyForValue(field, raw) : triggerFollowBranchKey(field, checklist);
  if ((field.type === "dropdown" || field.type === "yes_no") && !selected) return;
  const branchLabel = labelForParentFollowBranch(field.type, selected);
  collectVisibleFollowUpRows(
    field.optionFollowUps,
    selected,
    field.type,
    checklist,
    fieldVerified,
    `${pathPrefix} (if “${branchLabel}”)`,
    rows,
    1,
    getFollowUpValue,
    getFollowUpVerifyKey
  );
}

function collectFieldRows(
  field: ChecklistFieldDef,
  raw: string | undefined,
  verifyKey: string,
  pathPrefix: string,
  checklist: Record<string, string>,
  fieldVerified: Record<string, string>,
  rows: ExportFieldRow[],
  getFollowUpValue?: (valueKey: string) => string,
  getFollowUpVerifyKey?: (valueKey: string) => string
): void {
  pushRow(rows, verifyKey, pathPrefix, field.type, raw, fieldVerified, verifyKey, field);
  collectFieldFollowUpRows(
    field,
    raw,
    checklist,
    fieldVerified,
    pathPrefix,
    rows,
    getFollowUpValue,
    getFollowUpVerifyKey
  );
}

function repeatableRowHasMeaningfulData(
  row: Record<string, string>,
  fields: ChecklistFieldDef[],
  sectionId: string,
  groupId: string,
  rowIndex: number,
  fieldVerified: Record<string, string>
): boolean {
  return fields.some((f) => {
    const verified = verifyFlag(
      fieldVerified,
      repeatableRowVerifyKey(sectionId, groupId, rowIndex, f.id),
      f
    );
    return rowIsMeaningful(f.type, row[f.id], verified);
  });
}

function buildRepeatableGroupBlock(
  section: ChecklistSectionDef,
  g: ChecklistRepeatableGroupDef,
  checklist: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  fieldVerified: Record<string, string>,
  blockTitle?: string
): ExportSubBlock | null {
  const rk = repeatableEntriesKey(section.id, g.id);
  const allRows = repeatableValues[rk] || [];
  const rowIndices =
    g.deedMasterId != null ? sortRepeatableRowIndices(allRows) : allRows.map((_, idx) => idx);
  const outRows: ExportFieldRow[] = [];

  rowIndices.forEach((rowIdx, displayIdx) => {
    const row = allRows[rowIdx];
    if (!repeatableRowHasMeaningfulData(row, g.fields, section.id, g.id, rowIdx, fieldVerified)) {
      return;
    }
    const rowPrefix = rowIndices.length > 1 ? `Entry #${displayIdx + 1}` : "";
    g.fields.forEach((field) => {
      const verifyKey = repeatableRowVerifyKey(section.id, g.id, rowIdx, field.id);
      const getRowFollowUpValue = (valueKey: string) =>
        checklist[`${repeatableEntriesKey(section.id, g.id)}::${rowIdx}::fu::${valueKey}`] ?? "";
      const getRowFollowUpVerifyKey = (valueKey: string) =>
        `${repeatableEntriesKey(section.id, g.id)}::${rowIdx}::fu::${valueKey}`;
      collectFieldRows(
        field,
        row[field.id],
        verifyKey,
        rowPrefix ? `${rowPrefix} → ${field.label}` : field.label,
        checklist,
        fieldVerified,
        outRows,
        getRowFollowUpValue,
        getRowFollowUpVerifyKey
      );
    });
  });

  if (!outRows.length) return null;
  return { title: blockTitle, rows: outRows };
}

function buildBankProductBlocks(
  section: ChecklistSectionDef,
  checklist: Record<string, string>,
  fieldVerified: Record<string, string>
): ExportSubBlock[] {
  const blocks: ExportSubBlock[] = [];
  bankLinkedGroups(section).forEach((group) => {
    const rows: ExportFieldRow[] = [];
    group.fields.forEach((field) => {
      collectFieldRows(
        field,
        checklist[field.id],
        field.id,
        field.label,
        checklist,
        fieldVerified,
        rows
      );
    });
    if (rows.length) blocks.push({ title: group.label, rows });
  });
  return blocks;
}

function buildEcVerificationBlocks(
  section: ChecklistSectionDef,
  checklist: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  fieldVerified: Record<string, string>
): ExportSubBlock[] {
  const blocks: ExportSubBlock[] = [];
  const ecGroupFieldIds = new Set(
    ecLinkedGroups(section).flatMap((g) => g.fields.map((f) => f.id))
  );
  const summaryFields = section.fields.filter((f) => !ecGroupFieldIds.has(f.id));
  if (summaryFields.length) {
    const rows: ExportFieldRow[] = [];
    summaryFields.forEach((field) => {
      collectFieldRows(
        field,
        checklist[field.id],
        field.id,
        field.label,
        checklist,
        fieldVerified,
        rows
      );
    });
    if (rows.length) blocks.push({ title: "EC verification summary", rows });
  }

  const blockGroup = ecBlockGroup(section);
  const entryGroup = ecEntryGroup(section);
  if (!blockGroup) return blocks;

  const blockKey = repeatableEntriesKey(section.id, blockGroup.id);
  const blockRows = repeatableValues[blockKey] || [];
  blockRows.forEach((blockRow, blockIdx) => {
    const blockId = String(blockRow[EC_HIERARCHY_META.BLOCK_ID] ?? "").trim() || `block-${blockIdx}`;
    const blockTitle = `EC ${blockIdx + 1}${
      blockRow[EC_HIERARCHY_META.BLOCK_POS]
        ? ` (Sl.No ${blockRow[EC_HIERARCHY_META.BLOCK_POS]})`
        : ""
    }`;
    const headerRows: ExportFieldRow[] = [];
    blockGroup.fields.forEach((field) => {
      const verifyKey = repeatableRowVerifyKey(section.id, blockGroup.id, blockIdx, field.id);
      const getRowFollowUpValue = (valueKey: string) =>
        checklist[`${repeatableEntriesKey(section.id, blockGroup.id)}::${blockIdx}::fu::${valueKey}`] ??
        "";
      const getRowFollowUpVerifyKey = (valueKey: string) =>
        `${repeatableEntriesKey(section.id, blockGroup.id)}::${blockIdx}::fu::${valueKey}`;
      collectFieldRows(
        field,
        blockRow[field.id],
        verifyKey,
        field.label,
        checklist,
        fieldVerified,
        headerRows,
        getRowFollowUpValue,
        getRowFollowUpVerifyKey
      );
    });
    if (headerRows.length) {
      blocks.push({ title: `${blockTitle} — document details`, rows: headerRows });
    }

    if (!entryGroup) return;
    const entryRows = entryRowsForBlock(section.id, repeatableValues, blockId).filter(
      (r) => r[EC_HIERARCHY_META.RELATED] === "true"
    );
    entryRows.forEach((entryRow) => {
      const lineIndex = String(entryRow[EC_HIERARCHY_META.LINE_INDEX] ?? "");
      const entryKey = repeatableEntriesKey(section.id, entryGroup.id);
      const allEntryRows = repeatableValues[entryKey] || [];
      const rowIndex = allEntryRows.indexOf(entryRow);
      const outRows: ExportFieldRow[] = [];
      entryGroup.fields.forEach((field) => {
        const verifyKey =
          rowIndex >= 0
            ? repeatableRowVerifyKey(section.id, entryGroup.id, rowIndex, field.id)
            : field.id;
        const getRowFollowUpValue = (valueKey: string) =>
          rowIndex >= 0
            ? checklist[`${repeatableEntriesKey(section.id, entryGroup.id)}::${rowIndex}::fu::${valueKey}`] ??
              ""
            : "";
        const getRowFollowUpVerifyKey = (valueKey: string) =>
          rowIndex >= 0
            ? `${repeatableEntriesKey(section.id, entryGroup.id)}::${rowIndex}::fu::${valueKey}`
            : valueKey;
        collectFieldRows(
          field,
          entryRow[field.id],
          verifyKey,
          field.label,
          checklist,
          fieldVerified,
          outRows,
          getRowFollowUpValue,
          getRowFollowUpVerifyKey
        );
      });
      if (outRows.length) {
        blocks.push({ title: `${blockTitle} — Entry ${lineIndex}`, rows: outRows });
      }
    });
  });

  return blocks;
}

function gdScheduleByIdMap(
  gdSchedules: GeneralDetailsScheduleRow[]
): Map<string, GeneralDetailsScheduleRow> {
  return new Map(gdSchedules.map((s) => [String(s.id ?? "").trim(), s] as const));
}

function gdNatureKeysForSchedules(gdSchedules: GeneralDetailsScheduleRow[]): Set<string> {
  const keys = new Set<string>();
  gdSchedules.forEach((sched) => {
    const nk = matchNaturePropertyKey(sched.nature_doc ?? "");
    if (nk) keys.add(nk);
  });
  return keys;
}

/** Skip stale hierarchy rows whose nature group disagrees with General Detail schedule. */
function hierarchyRowMatchesGdScheduleNature(
  row: Record<string, string>,
  group: ChecklistRepeatableGroupDef,
  gdScheduleById: Map<string, GeneralDetailsScheduleRow>,
  gdSchedules: GeneralDetailsScheduleRow[]
): boolean {
  const schedId = String(row[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim();
  const gdSched = schedId ? gdScheduleById.get(schedId) : undefined;
  const gdNature = gdSched ? matchNaturePropertyKey(gdSched.nature_doc ?? "") : null;
  const rowNature = String(row[SURVEY_HIERARCHY_META.NATURE_KEY] ?? "").trim();

  if (gdSchedules.length) {
    if (!schedId || !gdSched) return false;
    if (gdNature && group.naturePropertyKey && group.naturePropertyKey !== gdNature) return false;
    if (gdNature && rowNature && rowNature !== gdNature) return false;
    return true;
  }
  if (group.naturePropertyKey && rowNature && rowNature !== group.naturePropertyKey) {
    return false;
  }
  return true;
}

function hierarchyExportRows(
  section: ChecklistSectionDef,
  groupId: string,
  fdId: string,
  repeatableValues: Record<string, Record<string, string>[]>,
  parentMeta?: Record<string, string>,
  natureKey?: string,
  groupNatureKey?: string | null
): { row: Record<string, string>; rowIndex: number }[] {
  const key = repeatableEntriesKey(section.id, groupId);
  const all = repeatableValues[key] || [];
  const out: { row: Record<string, string>; rowIndex: number }[] = [];
  const expectedNature = String(natureKey ?? "").trim();
  const scopedGroupNature = String(groupNatureKey ?? "").trim();
  all.forEach((row, rowIndex) => {
    if (fdId && String(row[SURVEY_HIERARCHY_META.FD_ID] ?? "").trim() !== fdId) return;
    if (expectedNature) {
      const rowNature = String(row[SURVEY_HIERARCHY_META.NATURE_KEY] ?? "").trim();
      if (rowNature && rowNature !== expectedNature) return;
      if (!rowNature && scopedGroupNature && scopedGroupNature !== expectedNature) return;
    }
    if (parentMeta) {
      for (const mk of Object.keys(parentMeta)) {
        const expected = String(parentMeta[mk] ?? "").trim();
        if (expected && String(row[mk] ?? "").trim() !== expected) return;
      }
    }
    out.push({ row, rowIndex });
  });
  return out;
}

function findHierarchyExportRowByIdentity(
  section: ChecklistSectionDef,
  group: ChecklistRepeatableGroupDef,
  level: SurveyHierarchyLevel,
  repeatableValues: Record<string, Record<string, string>[]>,
  sourceRow: Record<string, string>,
  natureKey?: string
): { row: Record<string, string>; rowIndex: number } | null {
  const rk = repeatableEntriesKey(section.id, group.id);
  const all = repeatableValues[rk] || [];
  const identity = hierarchyRowIdentity(level, sourceRow);
  if (identity.replace(/\|/g, "").length) {
    for (let rowIndex = 0; rowIndex < all.length; rowIndex++) {
      const row = all[rowIndex];
      if (hierarchyRowIdentity(level, row) !== identity) continue;
      if (natureKey) {
        const rowNature = String(row[SURVEY_HIERARCHY_META.NATURE_KEY] ?? "").trim();
        if (rowNature && rowNature !== natureKey) continue;
        if (
          !rowNature &&
          group.naturePropertyKey &&
          group.naturePropertyKey !== natureKey
        ) {
          continue;
        }
      }
      return { row, rowIndex };
    }
  }
  const fdId = String(sourceRow[SURVEY_HIERARCHY_META.FD_ID] ?? "").trim();
  const parentMeta: Record<string, string> = {};
  if (level !== "schedule") {
    parentMeta[SURVEY_HIERARCHY_META.SCHEDULE_ID] = String(
      sourceRow[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? ""
    );
  }
  if (level === "survey" || level === "boundary" || level === "measurement") {
    parentMeta[SURVEY_HIERARCHY_META.SUR_ID] = String(
      sourceRow[SURVEY_HIERARCHY_META.SUR_ID] ?? ""
    );
  }
  if (level === "boundary" || level === "measurement") {
    parentMeta[SURVEY_HIERARCHY_META.BOUNDARY_ID] = String(
      sourceRow[SURVEY_HIERARCHY_META.BOUNDARY_ID] ?? ""
    );
  }
  if (level === "measurement") {
    parentMeta[SURVEY_HIERARCHY_META.MEASUREMENT_ID] = String(
      sourceRow[SURVEY_HIERARCHY_META.MEASUREMENT_ID] ?? ""
    );
  }
  const matches = hierarchyExportRows(
    section,
    group.id,
    fdId,
    repeatableValues,
    Object.keys(parentMeta).length ? parentMeta : undefined,
    natureKey,
    group.naturePropertyKey
  );
  return matches[0] ?? null;
}


function collectSavedSurveyNatureKeys(
  section: ChecklistSectionDef,
  fdId: string,
  checklist: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  fieldVerified: Record<string, string>,
  gdSchedules: GeneralDetailsScheduleRow[] = []
): Set<string> {
  const keys = new Set<string>();
  const gdScheduleById = gdScheduleByIdMap(gdSchedules);
  const gdNatures = gdNatureKeysForSchedules(gdSchedules);

  surveyHierarchyLinkedGroups(section).forEach((group) => {
    const rk = repeatableEntriesKey(section.id, group.id);
    (repeatableValues[rk] || []).forEach((row, rowIndex) => {
      if (fdId && String(row[SURVEY_HIERARCHY_META.FD_ID] ?? "").trim() !== fdId) return;
      if (!hierarchyRowMatchesGdScheduleNature(row, group, gdScheduleById, gdSchedules)) {
        return;
      }
      const schedId = String(row[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim();
      const gdSched = schedId ? gdScheduleById.get(schedId) : undefined;
      const gdNature = gdSched ? matchNaturePropertyKey(gdSched.nature_doc ?? "") : null;
      const rowNature = String(row[SURVEY_HIERARCHY_META.NATURE_KEY] ?? "").trim();
      const natureKey = gdNature || rowNature || group.naturePropertyKey || "";
      if (!natureKey) return;
      if (gdNatures.size && !gdNatures.has(natureKey)) return;
      if (
        !repeatableRowHasMeaningfulData(
          row,
          group.fields,
          section.id,
          group.id,
          rowIndex,
          fieldVerified
        )
      ) {
        return;
      }
      keys.add(natureKey);
    });
  });

  Object.keys(checklist).forEach((k) => {
    const m = /^sv:([^:]+):([^:]+):/.exec(k);
    if (!m || (fdId && m[1] !== fdId)) return;
    if (!String(checklist[k] ?? "").trim()) return;
    const sched = gdScheduleById.get(m[2]);
    const nk = matchNaturePropertyKey(sched?.nature_doc ?? "");
    if (!nk) return;
    if (gdNatures.size && !gdNatures.has(nk)) return;
    keys.add(nk);
  });
  return keys;
}

function resolveSurveyExportNatureKeys(
  section: ChecklistSectionDef,
  fdId: string,
  checklist: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  fieldVerified: Record<string, string>,
  gdSchedules: GeneralDetailsScheduleRow[] = []
): string[] {
  const gdNatures = gdNatureKeysForSchedules(gdSchedules);
  const saved = collectSavedSurveyNatureKeys(
    section,
    fdId,
    checklist,
    repeatableValues,
    fieldVerified,
    gdSchedules
  );

  if (gdNatures.size) {
    const fromGd = Array.from(gdNatures).filter((nk) => saved.has(nk));
    return fromGd.length ? fromGd : Array.from(gdNatures);
  }
  return saved.size ? Array.from(saved) : [];
}

function filterGdSchedulesForExport(
  schedules: GeneralDetailsScheduleRow[],
  section: ChecklistSectionDef,
  fdId: string,
  checklist: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  fieldVerified: Record<string, string>
): GeneralDetailsScheduleRow[] {
  const gdNatures = gdNatureKeysForSchedules(schedules);
  const savedNatureKeys = collectSavedSurveyNatureKeys(
    section,
    fdId,
    checklist,
    repeatableValues,
    fieldVerified,
    schedules
  );

  return schedules.filter((sched) => {
    const natureKey = matchNaturePropertyKey(sched.nature_doc ?? "") ?? "";
    if (!natureKey) return true;
    if (gdNatures.size && !gdNatures.has(natureKey)) return false;
    if (savedNatureKeys.size && !savedNatureKeys.has(natureKey)) return false;
    return true;
  });
}

function collectSurveyExportFdIds(
  section: ChecklistSectionDef,
  checklist: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>
): string[] {
  const fdIds = new Set<string>();
  surveyHierarchyLinkedGroups(section).forEach((g) => {
    const key = repeatableEntriesKey(section.id, g.id);
    (repeatableValues[key] || []).forEach((row) => {
      const fd = String(row[SURVEY_HIERARCHY_META.FD_ID] ?? "").trim();
      if (fd) fdIds.add(fd);
    });
  });
  Object.keys(checklist).forEach((k) => {
    const m = /^sv:([^:]+):/.exec(k);
    if (m) fdIds.add(m[1]);
  });
  return fdIds.size ? Array.from(fdIds) : [""];
}

function resolveSurveyExportFdIds(
  section: ChecklistSectionDef,
  checklist: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  gdByDoc?: Record<string, GeneralDetailsDocumentHierarchy>
): string[] {
  const fromSaved = collectSurveyExportFdIds(section, checklist, repeatableValues).filter(Boolean);
  if (fromSaved.length) return Array.from(new Set(fromSaved));
  if (gdByDoc) {
    return Object.keys(gdByDoc).filter(
      (fd) => Array.isArray(gdByDoc[fd]?.schedules) && gdByDoc[fd]!.schedules!.length > 0
    );
  }
  return [];
}

function collectHierarchyGroupExportRows(
  section: ChecklistSectionDef,
  group: ChecklistRepeatableGroupDef,
  row: Record<string, string>,
  rowIndex: number,
  fieldLabelPrefix: string,
  checklist: Record<string, string>,
  fieldVerified: Record<string, string>,
  outRows: ExportFieldRow[]
): void {
  const groupKey = repeatableEntriesKey(section.id, group.id);
  group.fields.forEach((field) => {
    const verifyKey = repeatableRowVerifyKey(section.id, group.id, rowIndex, field.id);
    const getRowFollowUpValue = (valueKey: string) =>
      checklist[`${groupKey}::${rowIndex}::fu::${valueKey}`] ?? "";
    const getRowFollowUpVerifyKey = (valueKey: string) => `${groupKey}::${rowIndex}::fu::${valueKey}`;
    collectFieldRows(
      field,
      row[field.id],
      verifyKey,
      `${fieldLabelPrefix} → ${field.label}`,
      checklist,
      fieldVerified,
      outRows,
      getRowFollowUpValue,
      getRowFollowUpVerifyKey
    );
  });
}

function collectNatureScheduleExportBlocks(
  section: ChecklistSectionDef,
  natureRaw: string | undefined | null,
  fdId: string,
  scheduleId: string,
  schedTitle: string,
  checklist: Record<string, string>,
  fieldVerified: Record<string, string>
): ExportSubBlock[] {
  const natureKey = matchNaturePropertyKey(natureRaw ?? "");
  if (!natureKey) return [];
  const blocks: ExportSubBlock[] = [];
  surveyGroupsVisibleForFile(section, natureKey).forEach((group) => {
    const outRows: ExportFieldRow[] = [];
    group.fields.forEach((field) => {
      const scopedKey = surveyNatureFieldScopeKey(fdId, scheduleId, field.id);
      const getScopedFollowUpValue = (valueKey: string) =>
        checklist[surveyNatureFieldScopeKey(fdId, scheduleId, valueKey)] ?? "";
      const getScopedFollowUpVerifyKey = (valueKey: string) =>
        surveyNatureFieldScopeKey(fdId, scheduleId, valueKey);
      collectFieldRows(
        field,
        checklist[scopedKey],
        scopedKey,
        field.label,
        checklist,
        fieldVerified,
        outRows,
        getScopedFollowUpValue,
        getScopedFollowUpVerifyKey
      );
    });
    if (outRows.length) {
      blocks.push({ title: `${schedTitle} — ${group.label}`, rows: outRows });
    }
  });
  return blocks;
}

function buildSurveyScheduleExportBlocks(
  section: ChecklistSectionDef,
  fdId: string,
  sched: GeneralDetailsScheduleRow,
  schedIdx: number,
  resolvedNatureKey: string,
  natureTitle: string,
  checklist: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  fieldVerified: Record<string, string>
): ExportSubBlock[] {
  const blocks: ExportSubBlock[] = [];
  const scheduleId = String(sched.id ?? "");
  const schedNum = scheduleDisplayNumber(sched, schedIdx);
  const schedTitle = `${natureTitle} — Schedule ${schedNum}`;
  const flatRows = flattenSurveyHierarchyRows({ schedules: [sched] }, fdId);

  const scheduleGroup = surveyHierarchyGroupForLevel(section, "schedule", resolvedNatureKey);
  if (scheduleGroup && surveyHierarchyGroupHasChecklistFields(scheduleGroup)) {
    const sourceScheduleRow =
      flatRows.schedule.find(
        (r) => String(r[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim() === scheduleId
      ) ?? flatRows.schedule[0];
    if (sourceScheduleRow) {
      const matched = findHierarchyExportRowByIdentity(
        section,
        scheduleGroup,
        "schedule",
        repeatableValues,
        sourceScheduleRow,
        resolvedNatureKey
      );
      if (matched) {
        const schedOut: ExportFieldRow[] = [];
        collectHierarchyGroupExportRows(
          section,
          scheduleGroup,
          matched.row,
          matched.rowIndex,
          schedTitle,
          checklist,
          fieldVerified,
          schedOut
        );
        if (schedOut.length) blocks.push({ title: schedTitle, rows: schedOut });
      }
    }
  }

  blocks.push(
    ...collectNatureScheduleExportBlocks(
      section,
      sched.nature_doc,
      fdId,
      scheduleId,
      schedTitle,
      checklist,
      fieldVerified
    )
  );

  const surveyGroup = surveyHierarchyGroupForLevel(section, "survey", resolvedNatureKey);
  const boundaryGroup = surveyHierarchyGroupForLevel(section, "boundary", resolvedNatureKey);
  const measurementGroup = surveyHierarchyGroupForLevel(section, "measurement", resolvedNatureKey);

  (sched.survey_details || []).forEach((sur, surIdx) => {
    const surId = String(sur.sur_id ?? "");
    const surveyTitle = `${schedTitle} — Survey ${surIdx + 1}`;
    const sourceSurveyRow =
      flatRows.survey.find(
        (r) =>
          String(r[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim() === scheduleId &&
          String(r[SURVEY_HIERARCHY_META.SUR_ID] ?? "").trim() === surId
      ) ??
      flatRows.survey.find(
        (r) => String(r[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim() === scheduleId
      );

    if (surveyGroup && surveyHierarchyGroupHasChecklistFields(surveyGroup) && sourceSurveyRow) {
      const matched = findHierarchyExportRowByIdentity(
        section,
        surveyGroup,
        "survey",
        repeatableValues,
        sourceSurveyRow,
        resolvedNatureKey
      );
      if (matched) {
        const outRows: ExportFieldRow[] = [];
        collectHierarchyGroupExportRows(
          section,
          surveyGroup,
          matched.row,
          matched.rowIndex,
          surveyTitle,
          checklist,
          fieldVerified,
          outRows
        );
        if (outRows.length) blocks.push({ title: surveyTitle, rows: outRows });
      }
    }

    (sur.boundary_details || []).forEach((bnd, bndIdx) => {
      const bndId = String(bnd.measu_id ?? "");
      const bndTitle = `${surveyTitle} — Boundary ${bndIdx + 1}`;
      const sourceBoundaryRow = flatRows.boundary.find(
        (r) =>
          String(r[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim() === scheduleId &&
          String(r[SURVEY_HIERARCHY_META.SUR_ID] ?? "").trim() === surId &&
          String(r[SURVEY_HIERARCHY_META.BOUNDARY_ID] ?? "").trim() === bndId
      );

      if (boundaryGroup && surveyHierarchyGroupHasChecklistFields(boundaryGroup) && sourceBoundaryRow) {
        const matched = findHierarchyExportRowByIdentity(
          section,
          boundaryGroup,
          "boundary",
          repeatableValues,
          sourceBoundaryRow,
          resolvedNatureKey
        );
        if (matched) {
          const outRows: ExportFieldRow[] = [];
          collectHierarchyGroupExportRows(
            section,
            boundaryGroup,
            matched.row,
            matched.rowIndex,
            bndTitle,
            checklist,
            fieldVerified,
            outRows
          );
          if (outRows.length) blocks.push({ title: bndTitle, rows: outRows });
        }
      }

      (bnd.measurement_list || []).forEach((meas, measIdx) => {
        const measId = String(meas.measurement_id ?? "");
        const measTitle = `${bndTitle} — Measurement ${measIdx + 1}`;
        const sourceMeasurementRow = flatRows.measurement.find(
          (r) =>
            String(r[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? "").trim() === scheduleId &&
            String(r[SURVEY_HIERARCHY_META.SUR_ID] ?? "").trim() === surId &&
            String(r[SURVEY_HIERARCHY_META.BOUNDARY_ID] ?? "").trim() === bndId &&
            String(r[SURVEY_HIERARCHY_META.MEASUREMENT_ID] ?? "").trim() === measId
        );

        if (
          measurementGroup &&
          surveyHierarchyGroupHasChecklistFields(measurementGroup) &&
          sourceMeasurementRow
        ) {
          const matched = findHierarchyExportRowByIdentity(
            section,
            measurementGroup,
            "measurement",
            repeatableValues,
            sourceMeasurementRow,
            resolvedNatureKey
          );
          if (matched) {
            const outRows: ExportFieldRow[] = [];
            collectHierarchyGroupExportRows(
              section,
              measurementGroup,
              matched.row,
              matched.rowIndex,
              measTitle,
              checklist,
              fieldVerified,
              outRows
            );
            if (outRows.length) blocks.push({ title: measTitle, rows: outRows });
          }
        }
      });
    });
  });

  return blocks;
}

function buildSurveyVerificationBlocksFromGd(
  section: ChecklistSectionDef,
  checklist: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  fieldVerified: Record<string, string>,
  gdByDoc: Record<string, GeneralDetailsDocumentHierarchy>
): ExportSubBlock[] {
  const blocks: ExportSubBlock[] = [];
  const fdList = resolveSurveyExportFdIds(section, checklist, repeatableValues, gdByDoc);
  fdList.forEach((fdId) => {
    const allSchedules = gdByDoc[fdId]?.schedules;
    if (!Array.isArray(allSchedules) || !allSchedules.length) return;
    const schedules = filterGdSchedulesForExport(
      allSchedules,
      section,
      fdId,
      checklist,
      repeatableValues,
      fieldVerified
    );
    if (!schedules.length) return;

    const schedulesByNature = new Map<
      string,
      { sched: GeneralDetailsScheduleRow; schedIdx: number }[]
    >();
    schedules.forEach((sched, schedIdx) => {
      const natureKey = matchNaturePropertyKey(sched.nature_doc ?? "") ?? "__unmatched__";
      if (!schedulesByNature.has(natureKey)) schedulesByNature.set(natureKey, []);
      schedulesByNature.get(natureKey)!.push({ sched, schedIdx });
    });

    schedulesByNature.forEach((items, natureKey) => {
      const resolvedNatureKey = natureKey === "__unmatched__" ? "" : natureKey;
      const natureTitle =
        natureKey === "__unmatched__"
          ? "Other schedules"
          : naturePropertyLabel(natureKey) || natureKey;
      items.forEach(({ sched, schedIdx }) => {
        blocks.push(
          ...buildSurveyScheduleExportBlocks(
            section,
            fdId,
            sched,
            schedIdx,
            resolvedNatureKey,
            natureTitle,
            checklist,
            repeatableValues,
            fieldVerified
          )
        );
      });
    });
  });
  return blocks;
}

function buildSurveyVerificationBlocksFromRepeatable(
  section: ChecklistSectionDef,
  checklist: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  fieldVerified: Record<string, string>,
  gdByDoc?: Record<string, GeneralDetailsDocumentHierarchy>
): ExportSubBlock[] {
  const blocks: ExportSubBlock[] = [];
  const fdList = resolveSurveyExportFdIds(section, checklist, repeatableValues, gdByDoc);
  fdList.forEach((fdId) => {
    const gdSchedules = Array.isArray(gdByDoc?.[fdId]?.schedules)
      ? gdByDoc![fdId]!.schedules!
      : [];
    const natureKeys = resolveSurveyExportNatureKeys(
      section,
      fdId,
      checklist,
      repeatableValues,
      fieldVerified,
      gdSchedules
    );
    if (!natureKeys.length) return;

    natureKeys.forEach((natureKey) => {
      const resolvedNatureKey = natureKey || "";
      const natureTitle = natureKey ? naturePropertyLabel(natureKey) : "Survey verification";
      const scheduleGroup = surveyHierarchyGroupForLevel(section, "schedule", resolvedNatureKey);
      if (!scheduleGroup || !surveyHierarchyGroupHasChecklistFields(scheduleGroup)) return;

      const scheduleRows = hierarchyExportRows(
        section,
        scheduleGroup.id,
        fdId,
        repeatableValues,
        undefined,
        resolvedNatureKey,
        scheduleGroup.naturePropertyKey
      );
      scheduleRows.forEach(({ row, rowIndex }, schedIdx) => {
        const scheduleId = String(row[SURVEY_HIERARCHY_META.SCHEDULE_ID] ?? rowIndex);
        const schedTitle = `${natureTitle} — Schedule ${scheduleDisplayNumber(
          { schedule_id: Number(row[SURVEY_HIERARCHY_META.SCHEDULE_POS] ?? schedIdx) },
          schedIdx
        )}`;
        const scheduleMeta = { [SURVEY_HIERARCHY_META.SCHEDULE_ID]: scheduleId };

        const schedOut: ExportFieldRow[] = [];
        collectHierarchyGroupExportRows(
          section,
          scheduleGroup,
          row,
          rowIndex,
          schedTitle,
          checklist,
          fieldVerified,
          schedOut
        );
        if (schedOut.length) blocks.push({ title: schedTitle, rows: schedOut });

        blocks.push(
          ...collectNatureScheduleExportBlocks(
            section,
            natureKey,
            fdId,
            scheduleId,
            schedTitle,
            checklist,
            fieldVerified
          )
        );

        const surveyGroup = surveyHierarchyGroupForLevel(section, "survey", resolvedNatureKey);
        const boundaryGroup = surveyHierarchyGroupForLevel(section, "boundary", resolvedNatureKey);
        const measurementGroup = surveyHierarchyGroupForLevel(
          section,
          "measurement",
          resolvedNatureKey
        );

        if (surveyGroup && surveyHierarchyGroupHasChecklistFields(surveyGroup)) {
          const surveyRows = hierarchyExportRows(
            section,
            surveyGroup.id,
            fdId,
            repeatableValues,
            scheduleMeta,
            resolvedNatureKey,
            surveyGroup.naturePropertyKey
          );
          surveyRows.forEach(({ row: surRow, rowIndex: surIdx }, surDisplayIdx) => {
            const surveyTitle = `${schedTitle} — Survey ${surDisplayIdx + 1}`;
            const surId = String(surRow[SURVEY_HIERARCHY_META.SUR_ID] ?? surIdx);
            const surveyMeta = {
              ...scheduleMeta,
              [SURVEY_HIERARCHY_META.SUR_ID]: surId,
            };
            const outRows: ExportFieldRow[] = [];
            collectHierarchyGroupExportRows(
              section,
              surveyGroup,
              surRow,
              surIdx,
              surveyTitle,
              checklist,
              fieldVerified,
              outRows
            );
            if (outRows.length) blocks.push({ title: surveyTitle, rows: outRows });

            if (boundaryGroup && surveyHierarchyGroupHasChecklistFields(boundaryGroup)) {
              const boundaryRows = hierarchyExportRows(
                section,
                boundaryGroup.id,
                fdId,
                repeatableValues,
                surveyMeta,
                resolvedNatureKey,
                boundaryGroup.naturePropertyKey
              );
              boundaryRows.forEach(({ row: bndRow, rowIndex: bndIdx }, bndDisplayIdx) => {
                const bndTitle = `${surveyTitle} — Boundary ${bndDisplayIdx + 1}`;
                const bndId = String(bndRow[SURVEY_HIERARCHY_META.BOUNDARY_ID] ?? bndIdx);
                const boundaryMeta = {
                  ...surveyMeta,
                  [SURVEY_HIERARCHY_META.BOUNDARY_ID]: bndId,
                };
                const bndOut: ExportFieldRow[] = [];
                collectHierarchyGroupExportRows(
                  section,
                  boundaryGroup,
                  bndRow,
                  bndIdx,
                  bndTitle,
                  checklist,
                  fieldVerified,
                  bndOut
                );
                if (bndOut.length) blocks.push({ title: bndTitle, rows: bndOut });

                if (measurementGroup && surveyHierarchyGroupHasChecklistFields(measurementGroup)) {
                  const measurementRows = hierarchyExportRows(
                    section,
                    measurementGroup.id,
                    fdId,
                    repeatableValues,
                    boundaryMeta,
                    resolvedNatureKey,
                    measurementGroup.naturePropertyKey
                  );
                  measurementRows.forEach(({ row: measRow, rowIndex: measIdx }, measDisplayIdx) => {
                    const measTitle = `${bndTitle} — Measurement ${measDisplayIdx + 1}`;
                    const measOut: ExportFieldRow[] = [];
                    collectHierarchyGroupExportRows(
                      section,
                      measurementGroup,
                      measRow,
                      measIdx,
                      measTitle,
                      checklist,
                      fieldVerified,
                      measOut
                    );
                    if (measOut.length) blocks.push({ title: measTitle, rows: measOut });
                  });
                }
              });
            }
          });
        }
      });
    });
  });
  return blocks;
}

function buildSurveyVerificationBlocks(
  section: ChecklistSectionDef,
  checklist: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  fieldVerified: Record<string, string>,
  context?: DynamicChecklistExportContext
): ExportSubBlock[] {
  const blocks: ExportSubBlock[] = [];
  const surveyGroupFieldIds = new Set(
    surveyLinkedGroups(section).flatMap((g) => g.fields.map((f) => f.id))
  );
  const hierarchyGroupFieldIds = new Set(
    surveyHierarchyLinkedGroups(section).flatMap((g) => g.fields.map((f) => f.id))
  );
  const summaryFields = section.fields.filter(
    (f) => !surveyGroupFieldIds.has(f.id) && !hierarchyGroupFieldIds.has(f.id)
  );
  if (summaryFields.length) {
    const rows: ExportFieldRow[] = [];
    summaryFields.forEach((field) => {
      collectFieldRows(field, checklist[field.id], field.id, field.label, checklist, fieldVerified, rows);
    });
    if (rows.length) blocks.push({ title: "Survey verification summary", rows });
  }

  const gdByDoc = context?.generalDetailsByDocument;
  if (surveySectionUsesHierarchy(section)) {
    if (gdByDoc && Object.keys(gdByDoc).length) {
      blocks.push(
        ...buildSurveyVerificationBlocksFromGd(
          section,
          checklist,
          repeatableValues,
          fieldVerified,
          gdByDoc
        )
      );
    } else {
      blocks.push(
        ...buildSurveyVerificationBlocksFromRepeatable(
          section,
          checklist,
          repeatableValues,
          fieldVerified,
          gdByDoc
        )
      );
    }
    return blocks;
  }

  const natureKeys = new Set<string>();
  Object.keys(checklist).forEach((k) => {
    const m = /^sv:([^:]+):/.exec(k);
    if (m) {
      const fd = m[1];
      const scheduleGroup = surveyHierarchyGroupForLevel(section, "schedule");
      if (scheduleGroup) {
        const rk = repeatableEntriesKey(section.id, scheduleGroup.id);
        (repeatableValues[rk] || []).forEach((row) => {
          if (String(row[SURVEY_HIERARCHY_META.FD_ID] ?? "").trim() === fd) {
            const nk = String(row[SURVEY_HIERARCHY_META.NATURE_KEY] ?? "").trim();
            if (nk) natureKeys.add(nk);
          }
        });
      }
    }
  });
  resolveVisibleSurveyNatureGroups(section, natureKeys).forEach((group) => {
    const rows: ExportFieldRow[] = [];
    group.fields.forEach((field) => {
      collectFieldRows(field, checklist[field.id], field.id, field.label, checklist, fieldVerified, rows);
    });
    if (rows.length) blocks.push({ title: group.label, rows });
  });

  return blocks;
}

function buildDeedDocumentEntryBlock(
  section: ChecklistSectionDef,
  entry: DeedDocumentEntryInOrder,
  checklist: Record<string, string>,
  fieldVerified: Record<string, string>
): ExportSubBlock | null {
  const { gate, group, rowIndex, row, displayIndex } = entry;
  if (!deedDocumentEntryVisible(entry, checklist)) return null;

  const gateOpen = (checklist[gate.id] ?? "No") === "Yes";
  const outRows: ExportFieldRow[] = [];
  collectFieldRows(
    gate,
    checklist[gate.id],
    gate.id,
    gate.label,
    checklist,
    fieldVerified,
    outRows
  );

  if (gateOpen || deedDocumentEntryVisible(entry, checklist)) {
    const docNo = String(row[REPEATABLE_DOC_NO_META_KEY] ?? "").trim();
    const rowPrefix = docNo
      ? `Document #${displayIndex + 1} (Doc No: ${docNo})`
      : `Document #${displayIndex + 1}`;
    group.fields.forEach((field) => {
      const verifyKey = repeatableRowVerifyKey(section.id, group.id, rowIndex, field.id);
      const getRowFollowUpValue = (valueKey: string) =>
        checklist[`${repeatableEntriesKey(section.id, group.id)}::${rowIndex}::fu::${valueKey}`] ?? "";
      const getRowFollowUpVerifyKey = (valueKey: string) =>
        `${repeatableEntriesKey(section.id, group.id)}::${rowIndex}::fu::${valueKey}`;
      collectFieldRows(
        field,
        row[field.id],
        verifyKey,
        `${rowPrefix} → ${field.label}`,
        checklist,
        fieldVerified,
        outRows,
        getRowFollowUpValue,
        getRowFollowUpVerifyKey
      );
    });
  }

  if (!outRows.length) return null;
  return { title: gate.label, rows: outRows };
}

function buildStandardSectionBlocks(
  section: ChecklistSectionDef,
  checklist: Record<string, string>,
  repeatableValues: Record<string, Record<string, string>[]>,
  fieldVerified: Record<string, string>
): ExportSubBlock[] {
  const blocks: ExportSubBlock[] = [];
  const gates = sectionGateFields(section);
  const groupFieldIds = fieldIdsInDeedGroups(section);
  const gateIds = new Set(gates.map((g) => g.id));
  const standaloneFields = section.fields.filter(
    (f) => !gateIds.has(f.id) && !groupFieldIds.has(f.id)
  );
  const plainGroups = (section.repeatableGroups || []).filter((g) => !g.deedMasterId);

  if (standaloneFields.length) {
    const rows: ExportFieldRow[] = [];
    standaloneFields.forEach((field) => {
      collectFieldRows(
        field,
        checklist[field.id],
        field.id,
        field.label,
        checklist,
        fieldVerified,
        rows
      );
    });
    if (rows.length) blocks.push({ rows });
  }

  plainGroups.forEach((g) => {
    const block = buildRepeatableGroupBlock(
      section,
      g,
      checklist,
      repeatableValues,
      fieldVerified,
      g.label
    );
    if (block) blocks.push(block);
  });

  collectDeedDocumentEntriesInSaveOrder(section, repeatableValues).forEach((entry) => {
    const block = buildDeedDocumentEntryBlock(section, entry, checklist, fieldVerified);
    if (block) blocks.push(block);
  });

  return blocks;
}

/**
 * Walks the dynamic checklist master and saved runtime values to produce a
 * read-only document suitable for preview, PDF, and Excel export.
 */
export function buildDynamicChecklistExport(
  master: ChecklistSectionDef[],
  dynamic: DynamicChecklistRuntimeValue,
  remarks?: string,
  context?: DynamicChecklistExportContext
): DynamicChecklistExportDocument {
  const { checklistValues, repeatableValues, fieldVerified } = dynamic;
  const sections: ExportSection[] = [];

  sortSections(master).forEach((section) => {
    const flow = sectionFlowMode(section);
    const blocks =
      flow === "bank_product"
        ? buildBankProductBlocks(section, checklistValues, fieldVerified)
        : flow === "ec_verification"
          ? buildEcVerificationBlocks(
              section,
              checklistValues,
              repeatableValues,
              fieldVerified
            )
          : flow === "survey_verification"
            ? buildSurveyVerificationBlocks(
                section,
                checklistValues,
                repeatableValues,
                fieldVerified,
                context
              )
            : buildStandardSectionBlocks(
              section,
              checklistValues,
              repeatableValues,
              fieldVerified
            );

    if (!blocks.length) return;
    sections.push({
      id: section.id,
      title: section.title,
      headerVariant: section.headerVariant,
      blocks,
    });
  });

  return {
    sections,
    remarks: remarks?.trim() ? remarks.trim() : undefined,
  };
}

export function exportDocumentHasContent(doc: DynamicChecklistExportDocument): boolean {
  if (doc.remarks) return true;
  return doc.sections.some((s) => s.blocks.some((b) => b.rows.length > 0));
}
